import {
  WebsiteCommunication,
  WebsiteCommunicationButton,
} from '@amzn/brand-registry-communication-service-type-script-client';
import { recordEvents } from 'src/util/record-communication-event';
import { getCommunicationStatus, setLocalStorageStatus } from 'src/util/local-storage';
import { logger } from 'src/util/logger';

type ButtonType = 'PRIMARY' | 'SECONDARY';

function createImage(imageUrl: string, communicationId: string): HTMLDivElement {

  const imageContainer = document.createElement('div');
  imageContainer.id = `br-spotlight-message-image-container-${communicationId}`;
  imageContainer.classList.add( 'br-spotlight-message-image-container');

  const image = document.createElement('img');
  image.id = `br-spotlight-message-image-${communicationId}`;
  image.src = imageUrl;
  image.classList.add('br-spotlight-message-image');

  image.onerror = () => {
    // if the image fails to load, remove the image element entirely
    const message = document.getElementById(`br-spotlight-message-${communicationId}`);
    message?.removeChild(imageContainer);
  };

  imageContainer.append(image);

  return imageContainer;
}

function createButton(
  commsButton: WebsiteCommunicationButton,
  buttonType: ButtonType,
  communicationId: string
): HTMLButtonElement {

  const {
    id,
    label,
    action: {
      config,
    },
  } = commsButton;

  const button = document.createElement('button');
  button.id = id;
  button.setAttribute('aria-label', label);
  const buttonText = document.createTextNode(label);
  button.append(buttonText);

  const isPrimary = buttonType === 'PRIMARY';

  let targetUrl: string | undefined;

  if (config) {
    targetUrl = JSON.parse(config)?.url;
  }

  button.onclick = () => {
    const eventType = isPrimary ? 'PRIMARY_CLICKED' : 'SECONDARY_CLICKED';

    setLocalStorageStatus(communicationId, 'ENGAGED');

    recordEvents([communicationId], eventType);

    if (targetUrl) {
      location.href = targetUrl;
    } else {
      logger.error('Unable to locate target URL from message config');
    }
  };
  button.append(buttonText);
  
  return button;
}

function createButtons(communication: WebsiteCommunication): HTMLDivElement {
  const buttonContainer = document.createElement('div');
  buttonContainer.id = 'br-spotlight-message-button-container';

  if (communication.primaryButton) {

    const primaryButton = createButton(communication.primaryButton, 'PRIMARY', communication.communicationId);
    primaryButton.classList.add('br-spotlight-primary-button');
    buttonContainer.append(primaryButton);
  } 

  if (communication.secondaryButton) {
    
    const secondaryButton = createButton(communication.secondaryButton, 'SECONDARY', communication.communicationId);
    secondaryButton.classList.add('br-spotlight-secondary-button');
    buttonContainer.append(secondaryButton);
  } 
    
  return buttonContainer;
}

export function renderSpotlightMessage(communication: WebsiteCommunication) {

  const commsId = communication.communicationId;

  const message = document.createElement('div');
  message.id = `br-spotlight-message-${commsId}`;
  message.classList.add('br-spotlight-message');

  const textContent = document.createElement('div');
  textContent.id = `br-spotlight-text-content-${commsId}`;
  textContent.classList.add(
    'tw-br-trim-space-y-3.5', 'br-spotlight-message-content', 'tw-br-trim-w-full'
  );

  const status = getCommunicationStatus(commsId, communication.status);
  
  if (status === 'SENT') {
    message.classList.add('tw-br-trim-bg-tertiary-lake');
  } else if (status === 'DISPLAYED') {
    message.classList.add('tw-br-trim-bg-tertiary-lake');
  } else {
    message.classList.add('hover:tw-br-trim-bg-zinc-100');
  }

  const imageURL = communication.image?.url;

  if (imageURL) {
    message.append(createImage(imageURL, communication.communicationId));
  }

  const messageText = document.createElement('div');
  messageText.id = 'br-spotlight-message-text';

  const header = document.createElement('h5');
  header.id = 'br-spotlight-message-header';
  header.classList.add('tw-br-trim-m-0', 'tw-br-trim-leading-tight');
  header.append(communication.header.text);

  const body = document.createElement('p');
  body.id = 'br-spotlight-message-body';
  body.classList.add('tw-br-trim-m-0','tw-br-trim-my-1.5','tw-br-trim-text-[13px]', 'tw-br-trim-leading-[18px]');
  body.append(communication.body.text);

  const footer = document.createElement('div');
  footer.id = 'br-spotlight-message-footer';
  footer.classList.add('tw-br-trim-text-[12px]', 'tw-br-trim-text-light', 'tw-br-trim-leading-[15px]');
  footer.append(communication.footer.text);

  messageText.append(header);
  messageText.append(body);
  messageText.append(footer);

  const cta = document.createElement('div');
  cta.id = 'br-spotlight-message-cta';
  cta.classList.add('br-spotlight-message-cta');
  cta.append(createButtons(communication));
  
  textContent.append(messageText);
  textContent.append(cta);
  
  message.append(textContent);
  
  return message;
}
